import { useEffect, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import type { StyleProp, ViewStyle } from 'react-native';

import { Text } from './Text';
import { useTheme } from '../theme/ThemeProvider';

export type InlineActionErrorProps = {
  message: string;
  trigger: number | null;
  millisecondsUntilFadeOut?: number;
  style?: StyleProp<ViewStyle>;
  testID?: string;
};

// Shown briefly next to a control whose command was accepted but then failed on the
// caller's device — the button's own disabled state already covers "not possible right now".
export function InlineActionError({
  message,
  trigger,
  millisecondsUntilFadeOut = 3000,
  style,
  testID,
}: InlineActionErrorProps) {
  const theme = useTheme();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (trigger === null) {
      return;
    }

    setVisible(true);
    const hideTimeout = setTimeout(() => setVisible(false), millisecondsUntilFadeOut);
    return () => clearTimeout(hideTimeout);
  }, [trigger, millisecondsUntilFadeOut]);

  if (!visible) {
    return null;
  }

  return (
    <View
      accessibilityRole="alert"
      testID={testID}
      style={[
        styles.bubble,
        {
          backgroundColor: theme.colors.dangerSubtle,
          borderRadius: theme.radius.sm,
          paddingVertical: theme.spacing(1),
          paddingHorizontal: theme.spacing(2),
        },
        style,
      ]}
    >
      <Text variant="label" color={theme.colors.onDangerSubtle}>
        {message}
      </Text>
    </View>
  );
}

const styles = StyleSheet.create({
  bubble: { position: 'absolute', top: '100%', alignSelf: 'center', zIndex: 1 },
});
